<template>
  <ak-card-header>
    <div v-if="isOwnAccount()" class="pull-right">
      <ak-tooltip :placement="'left'" :has-arrow="false">
        <template #trigger>
          <router-link
            :to="{ name: 'newUser' }"
            class="icon-action icon-action--inline icon-action--offset icon-plus color-action"
          >
          </router-link>
        </template>
        Add New
      </ak-tooltip>
      <ak-tooltip :placement="'left'" :has-arrow="false">
        <template #trigger>
          <a
            class="icon-action icon-action--inline icon-action--offset icon-cog color-grey-light"
            @click="changeWidget()"
          ></a>
        </template>
        Change Widget
      </ak-tooltip>
    </div>
    <router-link :to="{ name: 'users' }">
      <h4 class="color-grey">Team</h4>
    </router-link>
  </ak-card-header>
  <ak-card-body :size="'flush'" class="flex--column overflow-y h-full">
    <ak-table
      v-if="!isLoading"
      v-model:data="users"
      spacing="tiny"
      hoverable
      background
      hide-head
      :empty-state="emptyState"
      :class="{ 'h-full flex': !users.length }"
    >
      <template #default="{ row }">
        <ak-table-column field="row.contactDetails.email" :width="40">
          <router-link
            :to="{ name: 'editUser', params: { userId: row._id } }"
            @click="row.clicked = true"
          >
            <div :class="{ 'p-tiny': row.clicked, 'p-small': !row.clicked }">
              <div v-if="row.clicked" class="avatar-wrapper">
                <ak-loading-spinner :size="'small'" />
              </div>
              <div v-if="!row.clicked" class="avatar-wrapper">
                <ak-avatar :string="row.name" :size="38" />
              </div>
            </div>
          </router-link>
        </ak-table-column>
        <ak-table-column field="row.name">
          <router-link
            :to="{ name: 'editUser', params: { userId: row._id } }"
            @click="row.clicked = true"
          >
            {{ row.name || 'No name given' }}
          </router-link>
        </ak-table-column>
        <ak-table-column class="text-right pr-small" field="row.userLevel">
          <router-link
            :to="{ name: 'editUser', params: { userId: row._id } }"
            @click="row.clicked = true"
          >
            <ak-tag ghost nowrap> {{ showUserLevel(row) }} </ak-tag>
          </router-link>
        </ak-table-column>
      </template>
    </ak-table>
    <ak-display-table v-if="isLoading" cosy>
      <ak-display-table-row
        v-for="number in n"
        :key="number"
        class="c-skeleton-loading"
      >
        <ak-display-table-cell class="width-40">
          <div
            class="circle circle--large bg-grey-lighter c-skeleton-loading__item"
          ></div>
        </ak-display-table-cell>
        <ak-display-table-cell>
          <div class="c-faux-text width-150 c-skeleton-loading__item"></div>
        </ak-display-table-cell>
        <ak-display-table-cell class="text-right">
          <div class="c-faux-text width-75 c-skeleton-loading__item"></div>
        </ak-display-table-cell>
      </ak-display-table-row>
    </ak-display-table>
  </ak-card-body>
</template>

<script>
import { computed, ref, onMounted, defineComponent } from 'vue'
import CardHeader from '@akerolabs/akero-vue/src/components/cards/CardHeader.vue'
import CardBody from '@akerolabs/akero-vue/src/components/cards/CardBody.vue'
import Table from '@akerolabs/akero-vue/src/components/table/Table.vue'
import TableColumn from '@akerolabs/akero-vue/src/components/table/TableColumn.vue'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src/components/toast'
import { accountObj, agencyObj } from '@akerolabs/akero-vue/src/store'
import { isOwnAccount } from '@akerolabs/akero-vue/src/services/users'
import { userLevel } from '@akerolabs/akero-vue/src/filters/user'
import { execChangeHandler } from '../../../legacy/router/service'

export default defineComponent({
  name: 'AkWidgetTeam',
  components: {
    [CardHeader.name]: CardHeader,
    [CardBody.name]: CardBody,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
  },
  inheritAttrs: false,
  props: {
    /**
     * @type {Function}
     * @description Update the widget state
     */
    changeWidget: {
      type: Function,
      default: () => ({}),
    },
  },

  setup() {
    let n = 10
    const users = ref([])
    const isLoading = ref(true)

    /**
     * @description Makes an api request to get the users
     */
    const fetchUsers = async () => {
      isLoading.value = true
      let usersData = []
      const displayError = (ex) => {
        if (ex) console.log(ex)
        ToastProgrammatic.open({
          message: `Error failed to load users`,
          type: 'danger',
        })
      }
      try {
        const { data } = await request.get('/users', usersRequest.value)
        usersData = data.data
      } catch (ex) {
        usersData = []
        displayError(ex)
      }
      users.value = usersData
      isLoading.value = false
    }

    /**
     * @description Returns the request object for making an api request for users
     * @type {Object}
     */
    const usersRequest = computed(() => {
      return {
        params: {
          account: accountObj()._id,
          agency: agencyObj()._id,
          deleted: false,
        },
      }
    })

    // Show user level and format it
    const showUserLevel = (user) => {
      let account = user.accounts.find((acc) => {
        return acc.account === accountObj()._id
      })
      return account ? userLevel(account.userLevel) : undefined
    }

    /**
     *
     * @description Create empty state for table
     */
    const emptyState = {
      title: 'Users',
      icon: 'person',
      text: 'There are currently no users on this account',
      button: 'Add User',
      click: () => execChangeHandler({ name: 'users' }),
    }

    /**
     * @description On mount fetch users
     */
    onMounted(async () => {
      await fetchUsers()
    })

    return {
      fetchUsers,
      users,
      isLoading,
      isOwnAccount,
      showUserLevel,
      n,
      emptyState,
    }
  },
})
</script>
